/**
 * Componente de confirmación PIN para transacciones del casino
 * Basado en Front/components/pin-confirmation.tsx con lógica de 3 intentos
 */
"use client";

import { useState, useEffect } from "react";
import { Check, Lock, ChevronLeft, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLanguage } from "@/contexts/language-context";

interface TransactionDetails {
  balance: string;
  amount: string;
  recipient: string;
  method?: string;
  fee: string;
  total: string;
  error: string;
}

interface CasinoPinConfirmationProps {
  transactionType: string;
  transactionDetails: TransactionDetails;
  email: string;
  onSuccess: (pin: string, sessionToken?: string) => void;
  onCancel: () => void;
  onError?: (error: string) => void;
  onMaxAttemptsReached?: () => void; // Callback cuando se alcanzan 3 intentos
  compact?: boolean; // Modo compacto para usar dentro de Dialogs
}

export function CasinoPinConfirmation({
  transactionType,
  transactionDetails,
  email,
  onCancel,
  onSuccess,
  onError,
  onMaxAttemptsReached,
  compact = false,
}: CasinoPinConfirmationProps) {
  const [pin, setPin] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  
  const { t } = useLanguage();
  
  const maxAttempts = 3;
  const balance = transactionDetails.balance;
  const isTotalExceedsBalance = parseFloat(transactionDetails.total) > parseFloat(balance);
  
  // Auto-focus on PIN input
  useEffect(() => {
    const pinInput = document.getElementById("casino-pin-input");
    if (pinInput) pinInput.focus();
  }, []);

  // Validar PIN con NBC TALK PAY API
  const verifyPinWithNBC = async (userEmail: string, pinCode: string) => {
    try {
      // CRITICAL: Obtener client_ip para sistema de blacklist
      let clientIp = '';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        clientIp = ipData.ip;
      } catch (ipError) {
        console.warn('⚠️ No se pudo obtener client_ip:', ipError);
        // Continuar sin IP - el backend rechazará por seguridad
      }

      const response = await fetch('/api/verify_pin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          pin: pinCode,
          client_ip: clientIp,  // ✅ Incluir client_ip para blacklist
          create_session: true,  // ✅ CRITICAL: Solicitar creación de session token
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Error response:', data);
        return { success: false, message: data.message || 'Error en la verificación' };
      }

      return data;
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return { success: false, message: 'Error de conexión' };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pin) {
      const errorMsg = t('validation.pin_required');
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
      return;
    }

    if (pin.length < 4 || pin.length > 4) {
      const errorMsg = t('validation.invalid_pin');
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await verifyPinWithNBC(email, pin);

      if (response.success) {
        setErrorMessage("");
        // Extraer session token de la respuesta
        const sessionToken = response.session?.token;
        console.log("✅ [PIN Confirmation] Session token received:", sessionToken ? "YES" : "NO");
        onSuccess(pin, sessionToken);
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= maxAttempts) {
          const errorMsg = t('pin.incorrect_pin_final', { maxAttempts: maxAttempts.toString() });
          setErrorMessage(errorMsg);
          onError?.(errorMsg);
          onMaxAttemptsReached?.();
        } else {
          const remainingAttempts = maxAttempts - newAttempts;
          const errorMsg = t('pin.incorrect_pin_attempts', { attempts: remainingAttempts.toString() });
          setErrorMessage(errorMsg);
          onError?.(errorMsg);
          setPin(""); // Clear PIN after error
        }
        
        setIsSubmitting(false);
      }
    } catch (error) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= maxAttempts) {
        const errorMsg = t('pin.incorrect_pin_final', { maxAttempts: maxAttempts.toString() });
        setErrorMessage(errorMsg);
        onMaxAttemptsReached?.();
      } else {
        const remainingAttempts = maxAttempts - newAttempts;
        const errorMsg = t('pin.verification_error', { attempts: remainingAttempts.toString() });
        setErrorMessage(errorMsg);
        onError?.(errorMsg);
        setPin(""); // Clear PIN after error
      }
      
      setIsSubmitting(false);
    }
  };

  return (
    <div className={compact ? "" : "flex justify-center items-center min-h-[80vh]"}>
      <Card className={compact ? "w-full border-0 shadow-none" : "w-full max-w-md"}>
        {!compact && (
          <CardHeader className="text-center">
            <div className="mx-auto my-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl">{t('pin.title')}</CardTitle>
            <CardDescription>
              {transactionType === 'casino_withdrawal' 
                ? t('pin.description_withdrawal')
                : t('pin.description')
              }
            </CardDescription>
          </CardHeader>
        )}
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="casino-pin-input">{t('pin.security_pin')}</Label>
              <Input
                id="casino-pin-input"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                placeholder={t('pin.enter_pin')}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                className="text-center text-lg tracking-widest"
                autoFocus
                disabled={attempts >= maxAttempts}
              />
              
              {/* Mostrar intentos restantes */}
              <div className="text-sm text-muted-foreground text-center">
                {attempts > 0 && attempts < maxAttempts && (
                  <span className="text-orange-600">
                    {t('pin.attempts_remaining', { attempts: (maxAttempts - attempts).toString() })}
                  </span>
                )}
                {attempts >= maxAttempts && (
                  <span className="text-red-600">
                    {t('pin.no_attempts_left')}
                  </span>
                )}
              </div>
            </div>

            {/* Error message display */}
            {errorMessage && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium mb-2">{t('pin.transaction_summary')}</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('pin.type')}</span>
                  <span>
                    {transactionType === 'casino_withdrawal' 
                      ? t('pin.casino_withdrawal')
                      : t('pin.casino_deposit')
                    }
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('casino.amount')}</span>
                  <span>{transactionDetails.amount}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('pin.destination')}</span>
                  <span className="truncate max-w-[200px] text-right">
                    {transactionType === 'casino_withdrawal' 
                      ? t('pin.user_wallet')
                      : t('pin.casino_wallet')
                    }
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('casino.network_fee')}</span>
                  <span>{transactionDetails.fee}</span>
                </div>

                <div className="flex justify-between border-t pt-2 font-medium">
                  <span>{t('casino.total_to_send')}</span>
                  <span>{transactionDetails.total}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('pin.network')}</span>
                  <span>{t('pin.polygon_usdt')}</span>
                </div>

                {isTotalExceedsBalance && (
                  <div className="text-red-500 text-sm mt-2">
                    {t('pin.insufficient_balance', { balance })}
                  </div>
                )}
              </div>
            </div>
          </form>
        </CardContent>
        
        <CardFooter className="flex flex-col gap-2">
          <Button
            type="submit"
            className="w-full"
            onClick={handleSubmit}
            disabled={isSubmitting || isTotalExceedsBalance || attempts >= maxAttempts}
          >
            {isSubmitting ? (
              <>{t('pin.processing')}</>
            ) : attempts >= maxAttempts ? (
              <>{t('error.transaction_cancelled')}</>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {transactionType === 'casino_withdrawal' 
                  ? t('pin.confirm')
                  : t('pin.confirm_send')
                }
              </>
            )}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            className="w-full" 
            onClick={onCancel} 
            disabled={isSubmitting}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t('pin.cancel')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}